import type { RequestOptions } from '../client.js';
import type { ChainSlug, EvmChainSlug, StarknetChainSlug } from '../types/chains.js';
import {
  type BlockHeightData,
  type GasComparisonData,
  type BlockTransactionsData,
  type GasEstimateData,
  type BatchBlockResultsData,
  type BatchTransactionResultsData,
  type SimulateCallData,
  type GetBlockByNumberParams,
  type GetBlockByHashParams,
  type GetTransactionByHashParams,
  getBlockHeightParamsSchema,
  compareGasParamsSchema,
  getBlockTransactionsParamsSchema,
  estimateGasParamsSchema,
  getBlockByNumberParamsSchema,
  getBlockByHashParamsSchema,
  getTransactionByHashParamsSchema,
  simulateCallParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import { parseParams } from '../internals/validation.js';
import { ChainNamespace } from './base.js';

/** Block height and gas data */
export class CoreNamespace extends ChainNamespace {
  /**
   * Get the current block height for a chain.
   */
  async getBlockHeight(chain?: ChainSlug, options?: RequestOptions): Promise<BlockHeightData> {
    const validated = parseParams(getBlockHeightParamsSchema, { chain: this.resolve(chain) });
    return this.client.jsonRpc<BlockHeightData>('getBlockHeight', validated, options);
  }

  /**
   * Get gas fee comparison across all EVM chains.
   * Returns base fee, priority fee, and estimated USD cost per chain.
   */
  async getGasComparison(options?: RequestOptions): Promise<GasComparisonData> {
    const validated = parseParams(compareGasParamsSchema, {});
    return this.client.jsonRpc<GasComparisonData>('compareGas', validated, options);
  }

  /**
   * Get all transactions in a specific block.
   */
  async getBlockTransactions(
    chain: ChainSlug | undefined,
    blockNumber: number,
    options?: RequestOptions,
  ): Promise<BlockTransactionsData> {
    const validated = parseParams(getBlockTransactionsParamsSchema, {
      chain: this.resolve(chain),
      blockNumber,
    });
    return this.client.jsonRpc<BlockTransactionsData>('getBlockTransactions', validated, options);
  }

  /**
   * Fetch one or more blocks by height across any supported chains in a single
   * batch (max 50 items). Bitcoin is supported; its `block` field carries a
   * Bitcoin block height (integer), and the response contains the full Bitcoin
   * Core `getblock` payload — including a `confirmations` field that drifts
   * over time as new tip blocks arrive.
   */
  async getBlockByNumber(
    items: GetBlockByNumberParams,
    options?: RequestOptions,
  ): Promise<BatchBlockResultsData> {
    const validated = parseParams(getBlockByNumberParamsSchema, items);
    return this.client.jsonRpc<BatchBlockResultsData>('getBlockByNumber', validated, options);
  }

  /**
   * Fetch one or more blocks by hash across any supported chains in a single
   * batch (max 50 items). EVM and Solana chains use this method; Bitcoin's
   * JSON-RPC handler accepts hashes too, but no test vector currently exercises
   * it — Bitcoin block lookups generally go through `getBlockByNumber`.
   */
  async getBlockByHash(
    items: GetBlockByHashParams,
    options?: RequestOptions,
  ): Promise<BatchBlockResultsData> {
    const validated = parseParams(getBlockByHashParamsSchema, items);
    return this.client.jsonRpc<BatchBlockResultsData>('getBlockByHash', validated, options);
  }

  /**
   * Fetch one or more transactions by hash across any supported chains in a
   * single batch (max 50 items). Same batch shape as `getBlockByHash` —
   * pass an array of `{ chain, hash }` pairs.
   */
  async getTransactionByHash(
    items: GetTransactionByHashParams,
    options?: RequestOptions,
  ): Promise<BatchTransactionResultsData> {
    const validated = parseParams(getTransactionByHashParamsSchema, items);
    return this.client.jsonRpc<BatchTransactionResultsData>(
      'getTransactionByHash',
      validated,
      options,
    );
  }

  /**
   * Estimate gas (EVM) or fees (Starknet) for a transaction. EVM + Starknet.
   */
  async estimateGas(
    chain: EvmChainSlug | StarknetChainSlug | undefined,
    params: { to: string; from?: string; value?: string; data?: string },
    options?: RequestOptions,
  ): Promise<GasEstimateData> {
    const validated = parseParams(estimateGasParamsSchema, {
      chain: this.resolve(chain),
      ...params,
    });
    return this.client.jsonRpc<GasEstimateData>('estimateGas', validated, options);
  }

  /**
   * Simulate a read-only contract call without submitting a transaction.
   *
   * EVM chains return `{ chain, result }` (hex `eth_call` output). Starknet returns
   * `{ chain, contractAddress, entryPointSelector, calldata, result }`, mapping `to` →
   * contract address, `data` → entry-point selector, and `value` → a single calldata felt;
   * for multi-felt calldata use `starknet.call(...)`. Other chain types are rejected by the server.
   */
  async simulateCall(
    chain: EvmChainSlug | StarknetChainSlug | undefined,
    params: {
      to: string;
      data: string;
      from?: string;
      value?: string;
      block?: string;
      blockHeight?: number;
    },
    options?: RequestOptions,
  ): Promise<SimulateCallData> {
    const validated = parseParams(simulateCallParamsSchema, {
      chain: this.resolve(chain),
      ...params,
    });
    return this.client.jsonRpc<SimulateCallData>('simulateCall', validated, options);
  }
}
